import { useState, useCallback, useEffect, useRef } from 'react';
import { JobClient } from '@/lib/jobClient';
import { ImportJobPollingState, JobResult, JOB_STATUS } from '@/types';

interface UseImportJobPollingResult extends ImportJobPollingState {
  startPolling: (jobId: string) => void;
  stopPolling: () => void;
}

export function useImportJobPolling(): UseImportJobPollingResult {
  const [state, setState] = useState<ImportJobPollingState>({
    isPolling: false,
    status: null,
    error: null,
    recipeId: null,
    scrapeMethod: null,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentJobIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    currentJobIdRef.current = null;
    setState(prev => ({ ...prev, isPolling: false }));
  }, []);

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const response = await JobClient.getJobStatus(jobId);
      
      const result = response.result as JobResult | undefined;
      setState(prevState => ({
        ...prevState,
        status: response.status,
        error: response.errorMessage || null,
        recipeId: result?.id || prevState.recipeId,
        scrapeMethod: result?.scrapeMethod ?? null,
      }));

      if (response.status === JOB_STATUS.COMPLETE) {
        stopPolling();
      } else if (response.status === JOB_STATUS.FAILED) {
        stopPolling();
      }
    } catch (error) {
      console.error('Failed to poll job status:', error);
      setState(prevState => ({
        ...prevState,
        status: JOB_STATUS.FAILED,
        error: error instanceof Error ? error.message : 'Failed to poll job status',
      }));
      stopPolling();
    }
  }, [stopPolling]);

  const startPolling = useCallback((jobId: string) => {
    // Stop any existing polling
    stopPolling();
    
    currentJobIdRef.current = jobId;
    setState({
      isPolling: true,
      status: JOB_STATUS.PENDING,
      error: null,
      recipeId: null,
      scrapeMethod: null,
    });

    // Start polling every 2 seconds
    intervalRef.current = setInterval(() => {
      if (currentJobIdRef.current) {
        pollJobStatus(currentJobIdRef.current);
      }
    }, 2000);

    // Do initial poll immediately
    pollJobStatus(jobId);
  }, [pollJobStatus, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    ...state,
    startPolling,
    stopPolling,
  };
}